import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  downloadAuditCsv,
  getAuditFilterOptions,
  getAuditSummary,
  getGuideProUsage,
  listAuditLogs,
  type AuditLogEntry,
  type AuditLogFilters,
  type GuideProUsage,
} from '../api/admin';

const DAY_RANGES = [
  { label: '24 hours', value: 1 },
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const PAGE_SIZE = 50;

const surfaceBadge: Record<string, string> = {
  dashboard: 'bg-indigo-50 text-indigo-700',
  extension: 'bg-emerald-50 text-emerald-700',
  admin: 'bg-purple-50 text-purple-700',
  api: 'bg-slate-100 text-slate-600',
};

const categoryBadge: Record<string, string> = {
  guide_pro: 'bg-amber-50 text-amber-700',
  dashboard: 'bg-sky-50 text-sky-700',
  guides: 'bg-teal-50 text-teal-700',
  billing: 'bg-rose-50 text-rose-700',
  admin: 'bg-purple-50 text-purple-700',
  auth: 'bg-slate-100 text-slate-600',
};

function formatTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

function relativeTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value).getTime();
  if (Number.isNaN(parsed)) return '—';
  const seconds = Math.round((Date.now() - parsed) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/** "guide_pro.demo_created" reads better as "Demo created". */
function humanAction(action: string): string {
  const tail = action.includes('.') ? action.slice(action.indexOf('.') + 1) : action;
  const words = tail.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function hostOf(url: string | null): string {
  if (!url) return '—';
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function Badge({ text, palette }: { text: string; palette: Record<string, string> }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        palette[text] || 'bg-slate-100 text-slate-600'
      }`}
    >
      {text.replace(/_/g, ' ')}
    </span>
  );
}

export function AuditLogPage() {
  const [tab, setTab] = useState<'activity' | 'guide-pro'>('activity');
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');
  const [category, setCategory] = useState('');
  const [surface, setSurface] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(handle);
  }, [search]);

  // Any filter change puts the reader back on page 1 — paging into an offset
  // that no longer exists shows an empty table and looks like "no activity".
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, orgId, userId, category, surface, action, days]);

  const filters: AuditLogFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      organization_id: orgId || undefined,
      user_id: userId || undefined,
      category: category || undefined,
      surface: surface || undefined,
      action: action || undefined,
      days,
    }),
    [debouncedSearch, orgId, userId, category, surface, action, days],
  );

  const summaryQuery = useQuery({
    queryKey: ['audit', 'summary', days, orgId],
    queryFn: () => getAuditSummary(days, orgId || undefined),
  });

  const optionsQuery = useQuery({
    queryKey: ['audit', 'filters', days],
    queryFn: () => getAuditFilterOptions(days),
  });

  const logsQuery = useQuery({
    queryKey: ['audit', 'logs', filters, page],
    queryFn: () => listAuditLogs({ ...filters, page, page_size: PAGE_SIZE }),
    enabled: tab === 'activity',
  });

  const guideProQuery = useQuery({
    queryKey: ['audit', 'guide-pro', days, orgId],
    queryFn: () => getGuideProUsage(days, orgId || undefined),
    enabled: tab === 'guide-pro',
  });

  const summary = summaryQuery.data;
  const options = optionsQuery.data;
  const logs = logsQuery.data;

  const clearFilters = () => {
    setSearch('');
    setOrgId('');
    setUserId('');
    setCategory('');
    setSurface('');
    setAction('');
  };

  const hasFilters = Boolean(search || orgId || userId || category || surface || action);

  return (
    <div className="p-8 max-w-[1500px] mx-auto">
      <div className="mb-6 flex items-start justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Every action users take on the platform — dashboard screens they open, controls they
            press, changes they save, and Guide Pro recordings with the websites they ran on.
            Conversation content is never recorded here.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {DAY_RANGES.map((range) => (
              <option key={range.value} value={range.value}>
                Last {range.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => downloadAuditCsv(filters)}
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Headline numbers */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Actions" value={summary?.total_events?.toLocaleString() ?? '—'} />
        <StatCard label="Active users" value={summary?.active_users ?? '—'} />
        <StatCard label="Organizations" value={summary?.active_orgs ?? '—'} />
        <StatCard
          label="Dashboard actions"
          value={summary?.dashboard_events?.toLocaleString() ?? '—'}
        />
        <StatCard
          label="Guide Pro"
          value={summary?.guide_pro_events?.toLocaleString() ?? '—'}
          hint={
            summary ? `on ${summary.guide_pro_websites} website${summary.guide_pro_websites === 1 ? '' : 's'}` : undefined
          }
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-5 border-b border-slate-200">
        {([
          ['activity', 'Activity'],
          ['guide-pro', 'Guide Pro usage'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'activity' ? (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search user, organization, action, page, or website…"
              className="flex-1 min-w-[260px] px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="">All organizations</option>
              {(options?.organizations ?? []).map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white max-w-[220px]"
            >
              <option value="">All users</option>
              {(options?.users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="">All areas</option>
              {(options?.categories ?? []).map((c) => (
                <option key={c} value={c}>
                  {c.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <select
              value={surface}
              onChange={(e) => setSurface(e.target.value)}
              className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="">All surfaces</option>
              {(options?.surfaces ?? []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white max-w-[220px]"
            >
              <option value="">All actions</option>
              {(options?.actions ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            {hasFilters ? (
              <button
                onClick={clearFilters}
                className="px-3 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900"
              >
                Clear
              </button>
            ) : null}
          </div>

          {logsQuery.isLoading ? (
            <div className="text-center py-16 text-slate-400">Loading activity…</div>
          ) : logsQuery.isError ? (
            <div className="text-center py-16 text-red-600">Could not load the audit log.</div>
          ) : (logs?.items.length ?? 0) === 0 ? (
            <div className="text-center py-16 text-slate-400">
              No activity recorded for these filters.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Organization</th>
                    <th className="px-4 py-3">Surface</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">What</th>
                    <th className="px-4 py-3">Where</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(logs?.items ?? []).map((entry: AuditLogEntry) => (
                    <Fragment key={entry.id}>
                      <tr
                        onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                        className="cursor-pointer hover:bg-slate-50"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-slate-500" title={formatTime(entry.created_at)}>
                          {relativeTime(entry.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">
                            {entry.actor_name || entry.actor_email || entry.site_domain || 'Unknown'}
                          </div>
                          {entry.actor_email ? (
                            <div className="text-xs text-slate-500">{entry.actor_email}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{entry.org_name || '—'}</td>
                        <td className="px-4 py-3">
                          <Badge text={entry.surface} palette={surfaceBadge} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{humanAction(entry.action)}</div>
                          <Badge text={entry.category} palette={categoryBadge} />
                        </td>
                        <td className="px-4 py-3 text-slate-700 max-w-[260px] truncate" title={entry.target_label || ''}>
                          {entry.target_label || entry.target_type || '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-500 max-w-[240px] truncate">
                          {entry.website ? (
                            <span title={entry.website}>{hostOf(entry.website)}</span>
                          ) : (
                            <span title={entry.route || ''}>{entry.route || '—'}</span>
                          )}
                        </td>
                      </tr>
                      {expandedId === entry.id ? (
                        <tr className="bg-slate-50">
                          <td colSpan={7} className="px-6 py-4">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                              <div>
                                <div className="text-slate-500">Exact time</div>
                                <div className="text-slate-900">{formatTime(entry.created_at)}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Request</div>
                                <div className="text-slate-900">
                                  {entry.method || '—'} {entry.status_code ?? ''}
                                  {entry.duration_ms != null ? ` · ${entry.duration_ms}ms` : ''}
                                </div>
                              </div>
                              <div>
                                <div className="text-slate-500">Route</div>
                                <div className="text-slate-900 break-all">{entry.route || '—'}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Website</div>
                                <div className="text-slate-900 break-all">{entry.website || '—'}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Site</div>
                                <div className="text-slate-900">{entry.site_domain || '—'}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Role</div>
                                <div className="text-slate-900">{entry.actor_role || '—'}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">IP address</div>
                                <div className="text-slate-900">{entry.ip_address || '—'}</div>
                              </div>
                              <div>
                                <div className="text-slate-500">Target id</div>
                                <div className="text-slate-900 break-all">{entry.target_id || '—'}</div>
                              </div>
                            </div>
                            {Object.keys(entry.details || {}).length > 0 ? (
                              <pre className="mt-4 p-3 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-700 overflow-x-auto">
                                {JSON.stringify(entry.details, null, 2)}
                              </pre>
                            ) : null}
                            {entry.user_agent ? (
                              <div className="mt-3 text-[11px] text-slate-400 break-all">
                                {entry.user_agent}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
                <div className="text-slate-600">
                  {logs?.total.toLocaleString()} action{logs?.total === 1 ? '' : 's'} · page{' '}
                  {logs?.page} of {logs?.pages || 1}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!logs || page >= logs.pages}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* What the platform is being used for */}
          {summary && summary.top_actions.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-semibold text-slate-900 mb-3">Most common actions</h2>
                <div className="space-y-2">
                  {summary.top_actions.map((row) => (
                    <div key={row.label} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{row.label}</span>
                      <span className="font-semibold text-slate-900">{row.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-semibold text-slate-900 mb-3">Busiest users</h2>
                <div className="space-y-2">
                  {summary.top_users.map((row) => (
                    <div key={row.label} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 truncate max-w-[70%]">{row.label}</span>
                      <span className="font-semibold text-slate-900">{row.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <GuideProTab
          loading={guideProQuery.isLoading}
          error={guideProQuery.isError}
          data={guideProQuery.data}
        />
      )}
    </div>
  );
}

function GuideProTab({
  loading,
  error,
  data,
}: {
  loading: boolean;
  error: boolean;
  data: GuideProUsage | undefined;
}) {
  if (loading) return <div className="text-center py-16 text-slate-400">Loading Guide Pro usage…</div>;
  if (error) return <div className="text-center py-16 text-red-600">Could not load Guide Pro usage.</div>;
  if (!data || data.total_events === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        No Guide Pro activity in this period.
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">Who is using Guide Pro</h2>
          <p className="text-xs text-slate-500 mt-1">
            Recording sessions, saves and edits, with the websites each person worked on.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Websites</th>
              <th className="px-4 py-3 text-right">Actions</th>
              <th className="px-4 py-3">Last used</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.users.map((user) => (
              <tr key={`${user.user_id ?? ''}-${user.actor_email ?? ''}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">
                    {user.actor_name || user.actor_email || 'Unknown'}
                  </div>
                  <div className="text-xs text-slate-500">{user.org_name || '—'}</div>
                </td>
                <td className="px-4 py-3">
                  {user.websites.length === 0 ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {user.websites.slice(0, 4).map((site) => (
                        <span
                          key={site}
                          title={site}
                          className="rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[11px]"
                        >
                          {hostOf(site)}
                        </span>
                      ))}
                      {user.websites.length > 4 ? (
                        <span className="text-[11px] text-slate-500">
                          +{user.websites.length - 4} more
                        </span>
                      ) : null}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{user.events}</td>
                <td className="px-4 py-3 text-slate-500" title={formatTime(user.last_used_at)}>
                  {relativeTime(user.last_used_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">Websites Guide Pro ran on</h2>
          <p className="text-xs text-slate-500 mt-1">
            Pages captured during recordings, with query strings removed.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Website</th>
              <th className="px-4 py-3 text-right">Users</th>
              <th className="px-4 py-3 text-right">Actions</th>
              <th className="px-4 py-3">Last used</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.websites.map((site) => (
              <tr key={site.website}>
                <td className="px-4 py-3 max-w-[280px]">
                  <div className="font-medium text-slate-900 truncate" title={site.website}>
                    {hostOf(site.website)}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{site.website}</div>
                </td>
                <td className="px-4 py-3 text-right text-slate-700">{site.users}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{site.events}</td>
                <td className="px-4 py-3 text-slate-500" title={formatTime(site.last_used_at)}>
                  {relativeTime(site.last_used_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
